<template>
  <div class="volunteer-hours-container">
    <div class="volunteer-hours-card">
      <div class="header-section">
        <h2 class="title">My Volunteer Hours</h2>
        <div class="summary-card">
          <div class="summary-content">
            <v-icon color="primary" large class="mr-2"
              >mdi-clock-outline</v-icon
            >
            <div>
              <div class="summary-label">Total Hours Contributed</div>
              <div class="summary-value">
                {{ formatDifferent(totalHours) }} hours
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="hours-table-section">
        <div class="table-header">
          <h3 class="section-title">Hourly Breakdown</h3>
          <!-- <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            outlined
            dense
            class="search-field"
          ></v-text-field> -->
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredData"
          :items-per-page="10"
          :loading="loading"
          :search="search"
          mobile-breakpoint="0"
          class="elevation-1 hours-table"
          :footer-props="{
            'items-per-page-options': [5, 10, 15, 20]
          }"
        >
          <template v-slot:loading>
            <v-row justify="center" align="center" class="py-4">
              <v-progress-circular
                indeterminate
                color="amber"
                v-if="loading"
                class="mt-10"
              />
            </v-row>
          </template>

          <template v-slot:[`item.hours`]="{ item }">
            <div class="hours-cell">
              <v-icon small class="mr-1">mdi-clock</v-icon>
              {{ formatDifferent(item.minutes) }} hrs
            </div>
          </template>

          <template v-slot:[`item.date`]="{ item }">
            <div class="date-cell">
              {{ formatTime(item.start) }}
            </div>
          </template>

          <template v-slot:[`item.shift`]="{ item }">
            <v-chip small v-if="item.shift" color="primary" text-color="white">
              {{ item.shift.title }}
            </v-chip>
            <span v-else>-</span>
          </template>

          <template v-slot:[`item.need`]="{ item }">
            <div>
              <v-btn
                v-if="item.need"
                @click="goToNeed(item.need._id)"
                text
                small
                color="primary"
                class="px-1"
              >
                {{ item.need.title }}
              </v-btn>
              <span v-else>-</span>
            </div>
          </template>

          <template v-slot:[`item.agency`]="{ item }">
            <div>
              <v-btn
                v-if="item.agency"
                @click="goToAgency(item.agency._id)"
                text
                small
                color="primary"
                class="px-1"
              >
                {{ item.agency.name }}
              </v-btn>
              <span v-else class="deleted-text">Deleted Agency</span>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="no-data-message">
              <v-icon class="mr-2">mdi-information-outline</v-icon>
              No volunteer hours recorded yet
            </div>
          </template>
        </v-data-table>
      </div>
    </div>

    <v-snackbar v-model="snackbar" :timeout="5000" color="error" top>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import dateFormat from "dateformat";

export default {
  data() {
    return {
      loading: false,
      errorMessage: null,
      snackbar: false,
      data: [],
      search: "",
      headers: [
        { text: "Hours", value: "hours", width: "120px" },
        { text: "Date", value: "date", width: "180px" },
        { text: "Shift", value: "shift", sortable: false },
        { text: "Need", value: "need", sortable: false },
        { text: "Agency", value: "agency", sortable: false }
      ]
    };
  },
  computed: {
    ...mapGetters({
      timezone: "usersetting/getTimeZone"
    }),
    totalHours() {
      return this.data.reduce((a, b) => a + b.minutes, 0);
    },
    filteredData() {
      if (!this.search) return this.data;
      const searchTerm = this.search.toLowerCase();
      return this.data.filter(item => {
        return (
          (item.shift && item.shift.title.toLowerCase().includes(searchTerm)) ||
          (item.need && item.need.title.toLowerCase().includes(searchTerm)) ||
          (item.agency &&
            item.agency.name.toLowerCase().includes(searchTerm)) ||
          this.formatTime(item.start)
            .toLowerCase()
            .includes(searchTerm)
        );
      });
    }
  },
  methods: {
    ...mapActions("volunteer", {
      getWorkHours: "getWorkHours"
    }),
    goToAgency(id) {
      this.$router.push({
        name: "agency-profile-details",
        params: { id }
      });
    },
    goToNeed(id) {
      this.$router.push({
        name: "agency-need-details",
        query: { _id: id }
      });
    },
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },
    formatDifferent(diff) {
      const hrs = Math.floor(diff / 60);
      const mins = Math.floor(diff % 60);
      return `${hrs}:${mins < 10 ? "0" + mins : mins}`;
    },
    formatTime(date) {
      return dateFormat(
        this.convertTZ(date, this.timezone),
        "mm/dd/yyyy hh:MM TT"
      );
    },
    convertTZ(date, tzString) {
      if (!date) {
        return new Date(
          new Date().toLocaleString("en-US", {
            timeZone: tzString ? tzString : "America/New_York"
          })
        );
      }
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString ? tzString : "America/New_York"
          }
        )
      );
    }
  },
  async mounted() {
    this.loading = true;
    try {
      const response = await this.getWorkHours();
      this.data = response;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to load volunteer hours";
      this.showErrorMessage(message);
    } finally {
      this.loading = false;
    }
  }
};
</script>

<style scoped lang="scss">
.volunteer-hours-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.volunteer-hours-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 24px;
}

.header-section {
  margin-bottom: 32px;
}

.title {
  font-size: 1.8rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
}

.summary-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  border-radius: 10px;
  padding: 20px;
  max-width: 400px;
}

.summary-content {
  display: flex;
  align-items: center;
}

.summary-label {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.hours-table-section {
  margin-top: 32px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.section-title {
  font-size: 1.4rem;
  font-weight: 500;
  color: #333;
  margin: 0;
}

.search-field {
  max-width: 300px;
  min-width: 200px;
}

.hours-table {
  border-radius: 8px;
  overflow: hidden;
}

.hours-cell {
  display: flex;
  align-items: center;
}

.date-cell {
  white-space: nowrap;
}

.deleted-text {
  color: #999;
  font-style: italic;
}

.no-data-message {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #666;
}

// Responsive adjustments
@media (max-width: 768px) {
  .volunteer-hours-container {
    padding: 12px;
  }

  .volunteer-hours-card {
    padding: 16px;
  }

  .title {
    font-size: 1.5rem;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .search-field {
    width: 100%;
    max-width: none;
  }
}

@media (max-width: 480px) {
  .summary-card {
    width: 100%;
  }

  ::v-deep .v-data-table {
    font-size: 0.85rem;
  }

  ::v-deep .v-data-table-header th {
    font-size: 0.8rem !important;
    padding: 8px !important;
  }

  ::v-deep .v-data-table td {
    padding: 8px !important;
  }
}
</style>
